// Assume algoliasearch is available globally from a CDN script include.
// If not, it needs to be loaded via a script tag in the HTML.

export class AlgoliaSearchManager {
  constructor(
    appId,
    searchKey,
    indexName,
    initialFilters = null,
    initialSortByIndex = null,
  ) {
    this.searchClient = algoliasearch(appId, searchKey);
    this.postType = "grant"; // Store the post type
    this.currentAlgoliaIndexName = initialSortByIndex || indexName;
    this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);

    this.searchTimeout = null;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentQuery = "";
    this.algoliaFilters = null;
    this.currentPriceRangeFilter = null;
    this.currentFacetFilters = {}; // To store multiple facet filters
  }

  /**
   * Performs a search on Algolia.
   * @param {string} query - The search query.
   * @param {number} page - The page number for pagination.
   * @returns {Promise<{hits: Array, nbHits: number, nbPages: number, page: number}>}
   */
  async performSearch(query, page = 0) {
    this.currentQuery = query;
    this.currentPage = page;

    if (this.index.indexName !== this.currentAlgoliaIndexName) {
      this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);
    }

    const searchOptions = {
      hitsPerPage: 20,
      page: page,
      attributesToHighlight: ["organization_name", "title"],
    };

    let allFilters = [];

    // Always filter to only include Grants post type
    allFilters.push("post_type:Grants");

    if (this.algoliaFilters) {
      allFilters.push(this.algoliaFilters);
    }

    if (this.currentPriceRangeFilter) {
      allFilters.push(this.currentPriceRangeFilter);
    }

    for (const attribute in this.currentFacetFilters) {
      if (this.currentFacetFilters[attribute]) {
        allFilters.push(this.currentFacetFilters[attribute]);
      }
    }

    if (allFilters.length > 0) {
      searchOptions.filters = allFilters.join(" AND ");
    }

    try {
      const { hits, nbHits, nbPages, page } = await this.index.search(
        query,
        searchOptions,
      );
      this.totalPages = nbPages;
      this.currentPage = page;
      return { hits, nbHits, nbPages, page };
    } catch (err) {
      console.error("Algolia search error:", err);
      return { hits: [], nbHits: 0, nbPages: 0, page: 0 };
    }
  }

  escapeAlgoliaFilterValue(value) {
    if (typeof value !== "string") {
      return value;
    }
    const doc = new DOMParser().parseFromString(value, "text/html");
    const decodedValue = doc.documentElement.textContent;
    const needsQuotes = /[&()![\]|<>=:*_~^?/\\]|\s/.test(decodedValue);
    return needsQuotes ? `"${decodedValue}"` : decodedValue;
  }

  setAlgoliaPriceRangeFilter(rangeString) {
    if (!rangeString) {
      this.currentPriceRangeFilter = null;
      return;
    }
    const [minStr, maxStr] = rangeString.split(":");
    const min = Number(minStr);
    const max = Number(maxStr);

    if (isNaN(min)) {
      this.currentPriceRangeFilter = null;
      return;
    }

    if (maxStr === "" || maxStr === undefined) {
      this.currentPriceRangeFilter = `grant_amount >= ${min}`;
    } else if (!isNaN(max)) {
      this.currentPriceRangeFilter = `grant_amount >= ${min} AND grant_amount <= ${max}`;
    } else {
      this.currentPriceRangeFilter = null;
    }
  }

  setAlgoliaFilters(filters) {
    if (filters && typeof filters === "string" && filters.includes(":")) {
      const [attribute, value] = filters.split(":", 2);
      this.algoliaFilters = `${attribute}:${this.escapeAlgoliaFilterValue(value)}`;
    } else {
      this.algoliaFilters = filters;
    }
  }

  setAlgoliaFilter(attribute, value) {
    if (value) {
      this.currentFacetFilters[attribute] = `${attribute}:${value}`;
    } else {
      delete this.currentFacetFilters[attribute];
    }
  }

  getAlgoliaFacets(attribute) {
    const searchOptions = {
      facets: [attribute],
      hitsPerPage: 0,
      page: 0,
    };
    return this.index
      .search(this.currentQuery, searchOptions)
      .then((response) => {
        return response.facets[attribute] || {};
      });
  }

  setAlgoliaSortByIndex(indexName) {
    if (indexName) {
      this.currentAlgoliaIndexName = indexName;
    }
  }

  getAlgoliaCurrentQuery() {
    return this.currentQuery;
  }

  setAlgoliaYearFilter(year) {
    if (year) {
      this.currentFacetFilters["award_year"] = `award_year:${year}`;
    } else {
      delete this.currentFacetFilters["award_year"];
    }
  }

  clear() {
    this.currentQuery = "";
    this.currentPage = 0;
    this.currentPriceRangeFilter = null;
    this.currentFacetFilters = {};
    // Note: UI elements (like searchInput) and triggering searches are now handled by the coordinating module.
  }
}
